import map from 'lodash/map'
import toNumber from 'lodash/toNumber'
import React, { FC, useCallback, useContext, useEffect, useState } from 'react'

import SvgAngleRight from '@Assets/SVG/AngleRight'
import { BuyButtonV2 } from '@Components/BuyButton/v2'
import { Checkbox } from '@Components/FormElements/UI/Checkbox'
import { Link } from '@Components/Link'
import { NativeModal, NativeModalBody, NativeModalFooter, NativeModalHeader, useNativeModalActions } from '@Components/Modal/NativeModal/NativeModal'
import { ProductContext } from '@Components/Product/Context'
import { Button } from '@CoreComponents/Buttons/Button'
import { ProductShipping } from '@Domains/Product/Features/Display/ProductShipping'
import { ProductUSPsLegacy } from '@Domains/Product/Features/Display/ProductUSPsLegacy'
import { TitleAndPrice } from '@Domains/Product/Features/Display/TitleAndPrice/TitleAndPrice'
import GiftCardCustomAmount from '@Domains/Product/Features/GiftCard/GiftCardCustomAmount/GiftCardCustomAmount'
import GiftCardMessageInput from '@Domains/Product/Features/GiftCard/GiftCardMessageInput/GiftCardMessageInput'
import { Money } from '@Generated/graphql'
import { MetadataContext } from '@Globals/Metadata'
import { useTranslation } from '@Globals/Translations'

import styles from './GiftCardInfo.module.scss'

export enum GiftcardAmountOptions {
  SELECT_AMOUNT = 'SELECT_AMOUNT',
  CUSTOM_AMOUNT = 'CUSTOM_AMOUNT',
}

// TODO: Remove once graphQL is finished
const TEMPORARY_GIFTCARD_STORE = {
  predefinedValues: [],
  valueMax: {
    currencyCode: '',
    value: 300000,
    format: '3000',
  },
  valueMin: {
    currencyCode: '',
    value: 2000,
    format: '20',
  },
}

interface GiftCardStoreProps {
  predefinedValues: Array<Money>
  valueMax: Money
  valueMin: Money
}

interface GiftCardPriceOptionProps {
  amountOptions: Array<{ key, value }>
  maxValue: number
  minValue: number
}

interface GiftCardInfoProps {
}

export const GiftCardInfoV2: FC<GiftCardInfoProps> = () => {
  const { t } = useTranslation()
  const product = useContext(ProductContext)
  const [message, setMessage] = useState<string | undefined>(undefined)
  const { store } = useContext(MetadataContext)
  const giftCardModalActions = useNativeModalActions()
  const isInStock = product?.availability?.isInStock
  const currencyFraction = store.config?.currencyFraction ?? 2
  const getPlainMoneyValue = useCallback((money: Money) => toNumber(`${money.value}`.slice(0, -currencyFraction)), [currencyFraction])
  const {
    predefinedValues,
    valueMax: giftCardStoreMax,
    valueMin: giftCardStoreMin,
  }: GiftCardStoreProps = store.config?.giftcards ?? TEMPORARY_GIFTCARD_STORE
  const [
    {
      amountOptions,
      maxValue,
      minValue,
    }, setGiftCardPriceOptions,
  ] = useState<GiftCardPriceOptionProps>({
    minValue: getPlainMoneyValue(giftCardStoreMin),
    maxValue: getPlainMoneyValue(giftCardStoreMax),
    amountOptions: [
      ...(predefinedValues?.map(money => ({
        key: `${getPlainMoneyValue(money)}`,
        value: money.format!,
      })) ?? []),
      {
        key: GiftcardAmountOptions.CUSTOM_AMOUNT,
        value: t('giftcard:GIFT_CARD_OTHER_AMOUNT', 'Other amount'),
      },
    ],
  })
  const [productPrice, setProductPrice] = useState<number>(0)
  const [customPrice, setCustomPrice] = useState<number>(0)
  const [selectedAmount, setSelectedAmount] = useState<string>(GiftcardAmountOptions.SELECT_AMOUNT)
  const [invalidCustomAmount, setInvalidCustomAmount] = useState<boolean>(false)
  const [shownAmount, setShownAmount] = useState<string>('g')
  const [shownAmountNum, setShownAmountNum] = useState<number>(0)
  const [shownPlaceHolder, setShownPlaceHolder] = useState<string>('h')

  useEffect(() => {
    setGiftCardPriceOptions((prev => ({
      ...prev,
      amountOptions: [
        ...(predefinedValues?.map(money => ({
          key: `${getPlainMoneyValue(money)}`,
          value: money.format!,
        })) ?? []),
        {
          key: GiftcardAmountOptions.CUSTOM_AMOUNT,
          value: t('giftcard:GIFT_CARD_OTHER_AMOUNT', 'Other amount'),
        },
      ],
    })))
  }, [getPlainMoneyValue, predefinedValues, t])

  if (!product) {
    return <>404</>
  }

  const selectGiftCardAmount = (value: string) => {
    setSelectedAmount(value)
    setProductPrice(value === GiftcardAmountOptions.CUSTOM_AMOUNT ? minValue : toNumber(value))
  }

  const otherOptions: string[] = [
    GiftcardAmountOptions.CUSTOM_AMOUNT,
    GiftcardAmountOptions.SELECT_AMOUNT,
  ]

  return (
    <>
      <NativeModal
        actionRef={giftCardModalActions}
        alignment="right"
        backdrop
        onClose={() => {
          setShownAmount(`${selectedAmount === GiftcardAmountOptions.CUSTOM_AMOUNT ? customPrice : shownPlaceHolder}`)
          setShownAmountNum(selectedAmount === GiftcardAmountOptions.CUSTOM_AMOUNT ? customPrice : productPrice)
          giftCardModalActions.current?.close()
        }}
      >
        <NativeModalHeader>{t('giftcard:GIFT_CARD_SELECT_AMOUNT')}</NativeModalHeader>
        <NativeModalBody
          className={styles.modalGiftSelectAmount}
        >
          <div className={styles.modalSelectWrapper}>
            {map(amountOptions, option => (
              <button
                key={`GiftCard_${option.key}`}
                className={styles.card}
                onClick={() => {
                  selectGiftCardAmount(option.key)
                  setInvalidCustomAmount(false)
                  setShownPlaceHolder(option.value)
                }}
                type="button"
              >
                <Checkbox
                  aria-label={option.key}
                  checked={selectedAmount === option.key}
                  className={styles.giftCardCheckbox}
                  id={`giftCardInfoOption${option.key}`}
                  label={option.value}
                  title={option.value}
                />
              </button>
            ))}
            {selectedAmount === GiftcardAmountOptions.CUSTOM_AMOUNT && (
              <GiftCardCustomAmount
                defaultValue={`${customPrice === 0 ? '' : customPrice}`}
                hasError={invalidCustomAmount}
                max={maxValue}
                min={minValue}
                onError={setInvalidCustomAmount}
                setValue={(v) => {
                  setSelectedAmount(GiftcardAmountOptions.CUSTOM_AMOUNT)
                  setCustomPrice(v)
                  setShownPlaceHolder(`${v}`)
                }}
              />
            )}
          </div>

        </NativeModalBody>
        <NativeModalFooter>
          <Button
            className={styles.buyButton}
            onClick={() => {
              setShownAmount(`${selectedAmount === GiftcardAmountOptions.CUSTOM_AMOUNT ? customPrice : shownPlaceHolder}`)
              setShownAmountNum(selectedAmount === GiftcardAmountOptions.CUSTOM_AMOUNT ? customPrice : productPrice)
              giftCardModalActions.current?.close()
            }}
            theme="black"
          >
            {t('cms:COF_SELECT')}
          </Button>
        </NativeModalFooter>
      </NativeModal>

      {product.brand && (
        <Link className={styles.brand} href={product.brand.url?.toString()}>
          <img
            alt={product.brand.id || ''}
            src={product.brand.logo || ''}
            title={product.brand.title || ''}
          />
        </Link>
      )}

      <div>
        <TitleAndPrice
          id={product.id}
          name={product.name}
        />

        {isInStock && (
          <div className={styles.addToCartWrapper}>
            <GiftCardMessageInput
              message={message || ''}
              onchange={setMessage}
              placeholder={t('giftcard:GIFT_CARD_MESSAGE_PLACEHOLDER', 'Your message goes here')}
            />

            <div className={styles.buyButtonWrapper}>
              <button
                className={`${styles.selectWrapper} ${otherOptions.includes(selectedAmount) ? styles.other : ''}`}
                id="giftcard-V2"
                onClick={() => {
                  giftCardModalActions.current?.open()
                }}
                type="button"
              >
                {(shownAmountNum < minValue || shownAmountNum > maxValue) && <p className={styles.textSelectButton}>{t('giftcard:GIFT_CARD_SELECT_AMOUNT')}</p>}
                {(shownAmountNum >= minValue && shownAmountNum <= maxValue)
                    && <p className={styles.textSelectButton}>{shownAmount}</p>}
                <SvgAngleRight className={styles.angleRight} />
              </button>

              <BuyButtonV2
                className={styles.buyButton}
                color="black"
                disabled={!selectedAmount || invalidCustomAmount}
                productID={product.id}
                products={[
                  ({
                    giftcard: {
                      amount: selectedAmount === GiftcardAmountOptions.CUSTOM_AMOUNT ? customPrice : productPrice,
                      text: message,
                    },
                    productID: product.id,
                    quantity: 1,
                  }),
                ]}
                quantity={1}
              >
                {t('cms:ADD_TO_CART')}
              </BuyButtonV2>
            </div>
          </div>
        )}
      </div>

      {isInStock && <ProductShipping />}

      <ProductUSPsLegacy />
    </>
  )
}

export default GiftCardInfoV2
